import { getInitials } from '@/utils';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * NewChatModal component for selecting contacts to start a new chat
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Array} props.contacts - Array of contact objects
 * @param {Function} props.onContactSelect - Callback when a contact is selected
 * @param {boolean} props.isLoading - Whether contacts are being loaded
 * @param {boolean} props.hasMore - Whether more contacts are available
 * @param {Function} props.onLoadMore - Callback to load more contacts
 * @param {Function} props.onSearch - Callback to search contacts
 * @returns {JSX.Element} NewChatModal component
 */
const NewChatModal = ({
  isOpen,
  onClose,
  contacts = [],
  onContactSelect,
  isLoading,
  hasMore,
  onLoadMore,
  onSearch,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const sentinelRef = useRef(null);
  const containerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Handle search with debounce
  const handleSearchChange = e => {
    const value = e.target.value;
    setSearchTerm(value);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      onSearch(value);
    }, 500);
  };

  const handleContactSelect = contact => {
    onContactSelect(contact);
    onClose();
    setSearchTerm('');
  };

  // Handle intersection for infinite scroll
  useEffect(() => {
    if (!hasMore || isLoading || !isOpen) return;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          onLoadMore();
        }
      },
      {
        root: containerRef.current,
        threshold: 0.1,
      }
    );

    const sentinel = sentinelRef.current;
    if (sentinel) {
      observer.observe(sentinel);
    }

    return () => {
      if (sentinel) {
        observer.unobserve(sentinel);
      }
    };
  }, [hasMore, isLoading, isOpen, onLoadMore]);

  // Handle keyboard events
  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  // Add keyboard listener when modal is open
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md max-h-[80vh] flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">New Chat</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 transition-colors focus:outline-none"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-100 bg-gray-50/50">
          <div className="relative">
            <input
              type="text"
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all"
              autoFocus
            />
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg
                className="h-4 w-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            {isLoading && (
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
              </div>
            )}
          </div>
        </div>

        {/* Contacts List */}
        <div ref={containerRef} className="flex-1 overflow-y-auto">
          {contacts.length === 0 && !isLoading ? (
            <div className="p-8 text-center text-gray-500">
              <div className="mb-2">
                <svg
                  className="w-12 h-12 mx-auto text-gray-300"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <p className="text-sm">
                {searchTerm ? 'No contacts found' : 'No contacts available'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {contacts.map(contact => (
                <div
                  key={contact.id || contact._id}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => handleContactSelect(contact)}
                >
                  <div className="flex items-center space-x-3">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {contact.avatar ? (
                        <img
                          src={contact.avatar}
                          alt={contact.name}
                          className="w-12 h-12 rounded-full object-cover border border-gray-100"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-primary-100 text-primary-700 rounded-full flex items-center justify-center border border-primary-200">
                          <span className="font-semibold text-base">
                            {getInitials(contact.name)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Contact Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <h3 className="text-sm font-semibold text-gray-900 truncate">
                          {contact.name}
                        </h3>
                        {contact.status === 'online' && (
                          <div className="w-2 h-2 rounded-full bg-green-500 border border-white" />
                        )}
                      </div>
                      <p className="text-xs text-gray-500 font-medium truncate">
                        {contact.phoneNumber || contact.phone || 'No phone'}
                      </p>
                      {contact.email && (
                        <p className="text-xs text-gray-400 truncate mt-0.5">
                          {contact.email}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Infinite Scroll Sentinel */}
              <div
                ref={sentinelRef}
                className="h-10 flex items-center justify-center"
              >
                {isLoading && hasMore && (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NewChatModal;
